/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// MixedChart configurations
import configs from "utils/Charts/MixedChart/configs";

// Material Dashboard 2 PRO React base styles
import colors from "assets/theme/base/colors";
import { numberFormatter } from "utils/HelperFunctions";

function MixedChart({ icon, title, description, height, chart, displayTrend, stacked = false }) {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({});
  const { data, options } = chartData;

  const getOrCreateTooltip = (chartObj) => {
    let tooltipEl = chartObj.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.style.background = "white";
      tooltipEl.style.borderRadius = "3px";
      tooltipEl.style.color = "black";
      tooltipEl.style.opacity = 1;
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transform = "translate(-50%, 0)";
      tooltipEl.style.transition = "all .1s ease";

      const table = document.createElement("table");
      table.style.margin = "0px";

      tooltipEl.style.zIndex = "99999";
      tooltipEl.appendChild(table);
      chartObj.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  const externalTooltipHandler = (context) => {
    // Tooltip Element
    const { chart: chrt, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chrt);
    tooltipEl.style.border = "1px solid #888";
    tooltipEl.style.borderRadius = "0.5rem";
    // tooltipEl.style.display = "inline-block";
    tooltipEl.style.padding = 0;

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    // Set Text
    if (tooltip.body) {
      const titleLines = tooltip.title || [];
      const bodyLines = tooltip.body.map((b) => b.lines);

      const tableHead = document.createElement("thead");

      titleLines.forEach((line) => {
        const tr = document.createElement("tr");
        tr.style.borderWidth = 0;

        const th = document.createElement("th");
        th.style.borderWidth = 0;
        th.style.fontSize = "0.9rem";
        th.style.whiteSpace = "nowrap";
        const text = document.createTextNode(line);

        th.appendChild(text);
        tr.appendChild(th);
        tableHead.appendChild(tr);
      });

      const sortedBodyLines = bodyLines.reduce((transformedLines, line, index) => {
        if (!line.length) return transformedLines;
        if (stacked) {
          const [name, total] = line[0].split("^");
          transformedLines.push({
            text: `${name}: ${total}`,
            total,
            color: tooltip.labelColors[index],
          });
        } else {
          transformedLines.push({ text: line, color: tooltip.labelColors[index] });
        }
        return transformedLines;
      }, []);

      if (stacked) {
        sortedBodyLines.sort((a, b) => b.total - a.total);
      }

      const tableBody = document.createElement("tbody");
      sortedBodyLines.forEach((line) => {
        const span = document.createElement("span");
        span.style.background = line.color.backgroundColor;
        span.style.borderColor = line.color.borderColor;
        span.style.borderWidth = "1px";
        span.style.marginRight = "10px";
        span.style.height = "10px";
        span.style.width = "10px";
        span.style.display = "inline-block";
        span.style.borderRadius = "5px";

        const tr = document.createElement("tr");
        tr.style.backgroundColor = "white";
        tr.style.borderWidth = 0;
        tr.style.lineHeight = "0.5rem";
        tr.style.whiteSpace = "nowrap";

        const td = document.createElement("td");
        td.style.borderWidth = 0;
        td.style.color = "#000";
        td.style.fontSize = "0.8rem";
        td.style.paddingTop = "3px";

        const text = document.createTextNode(line.text);

        td.appendChild(span);
        td.appendChild(text);
        tr.appendChild(td);
        tableBody.appendChild(tr);
      });

      const tableRoot = tooltipEl.querySelector("table");

      // Remove old children
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove();
      }

      // Add new children
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chrt.canvas;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
    tooltipEl.style.top = `${positionY + tooltip.caretY + 40}px`;
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
  };

  if (options) {
    options.scales.y.min = 0;
    options.plugins.tooltip = {
      enabled: false,
      position: "nearest",
      external: externalTooltipHandler,
    };
    options.scales.x.stacked = true;
    options.scales.y.stacked = false;

    if (stacked) {
      options.plugins.tooltip.callbacks = {
        label: (ctx) => {
          if (ctx.dataset.dailyCount && ctx.dataset.dailyCount[ctx.dataIndex]) {
            return `${ctx.dataset.label}^${numberFormatter(ctx.dataset.dailyCount[ctx.dataIndex])}`;
          }
          return "";
        },
        title: (ctx) => ctx[0]?.label,
        labelPointStyle: () => ({
          pointStyle: "circle",
        }),
        labelTextColor: () => "#333333",
      };
    }
  }

  useEffect(() => {
    let ymax = 10;
    let chartDatasets = chart.datasets
      ? chart.datasets.map((dataset) => {
          let finalConfigs;

          const defaultLine = {
            ...dataset,
            type: "line",
            tension: 0.3,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            borderColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            maxBarThickness: 6,
          };

          const trendLine = {
            ...dataset,
            type: "line",
            tension: 0.4,
            borderWidth: 1.5,
            pointRadius: 0,
            pointBackgroundColor: dataset.color ? dataset.color : colors.dark.main,
            borderColor: dataset.color ? dataset.color : colors.dark.main,
            maxBarThickness: 6,
            borderDash: [3, 3],
          };

          const gradientLine = {
            ...dataset,
            type: "line",
            tension: 0,
            pointRadius: 0,
            borderWidth: 4,
            borderColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            fill: true,
            maxBarThickness: 6,
            backgroundColor: gradientChartLine(
              chartRef.current.children[0],
              colors[dataset.color] ? colors[dataset.color || "dark"].main : colors.dark.main
            ),
          };

          const bar = {
            ...dataset,
            type: "bar",
            weight: 5,
            borderWidth: 0,
            borderRadius: 4,
            backgroundColor: dataset.color ? dataset.color : colors.dark.main,
            fill: false,
            maxBarThickness: 35,
          };

          const stackedBar = {
            ...dataset,
            type: "bar",
            weight: 5,
            borderWidth: 0,
            borderRadius: 0,
            backgroundColor: dataset.color,
            fill: false,
            maxBarThickness: 35,
          };

          const outOfOffice = {
            ...dataset,
            type: "bar",
            weight: 5,
            borderWidth: 0,
            borderRadius: 0,
            backgroundColor: dataset.color,
            fill: false,
            maxBarThickness: 35,
          };

          const thinBar = {
            ...dataset,
            type: "bar",
            weight: 5,
            borderWidth: 0,
            borderRadius: 4,
            backgroundColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            fill: false,
            maxBarThickness: 10,
          };

          if (dataset.chartType === "default-line") {
            finalConfigs = defaultLine;
          } else if (dataset.chartType === "gradient-line") {
            finalConfigs = gradientLine;
          } else if (dataset.chartType === "thin-bar") {
            finalConfigs = thinBar;
          } else if (dataset.chartType === "trend-line") {
            finalConfigs = trendLine;
          } else if (dataset.chartType === "stacked-bar") {
            finalConfigs = stackedBar;
          } else if (dataset.chartType === "outOfOffice") {
            finalConfigs = outOfOffice;
          } else {
            finalConfigs = bar;
          }

          if (dataset.ymax && dataset.ymax > ymax) {
            ymax = dataset.ymax;
          }

          return { ...finalConfigs };
        })
      : [];

    // Filter trendline
    if (!displayTrend) {
      chartDatasets = chartDatasets.filter((set) => set.chartType !== "trend-line");
    }

    // Remove total performance for stacked bar
    if (stacked) chartDatasets.shift();

    setChartData(configs(chart.labels || [], chartDatasets, ymax));
  }, [chart, displayTrend]);

  // const plugins = useMemo(
  //   () => [
  //     {
  //       id: "customIconPlugin",
  //       afterDatasetDraw: (chartObj) => {
  //         const { ctx } = chartObj;
  //         const meta = chartObj.getDatasetMeta(0);
  //         meta.data.forEach((dataPoint, index) => {
  //           if (chartObj?.data?.datasets?.[0]?.outOfOffice?.[index]) {
  //             const { x, y } = dataPoint.getProps(["x", "y"]);
  //             // Draw the icon
  //             ctx.save();
  //             ctx.fillStyle = theme.palette.gray20.main;
  //             ctx.font = '3rem "Material Symbols Outlined"';
  //             ctx.textAlign = "center";
  //             ctx.textBaseline = "bottom";
  //             ctx.fillText("event_busy", x, y - 10);
  //             ctx.restore();
  //           }
  //         });
  //       },
  //     },
  //   ],
  //   [theme]
  // );

  const renderChart = (
    <MDBox pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "info"}
              variant="gradient"
              coloredShadow={icon.color || "info"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      {useMemo(
        () => (
          <MDBox ref={chartRef} sx={{ height }}>
            <Line data={data} options={options} />
          </MDBox>
        ),
        [chartData, height]
      )}
    </MDBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of MixedChart
MixedChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
};

// Typechecking props for the MixedChart
MixedChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
};

export default MixedChart;
